import { runInAction } from 'mobx';
import axios from 'axios';
import ImportacaoApi from '@/api/ImportacaoApi';
import ImportacaoService from '@/services/ImportacaoService';
import MinutaService from '@/services/MinutaService';
import CategoriaMinutaService from '@/services/CategoriaMinutaService';
import ProcessoService from '@/services/ProcessoService';
import EscaninhoService from '@/services/EscaninhoService';
import UsuarioStore from '@/store/UsuarioStore';
import ControleGabineteStore from '@/store/ControleGabineteStore';
import ImportacaoLoteStore, { IModeloSelecionado, IProcessoCSV } from '@/store/ImportacaoLoteStore';
import { CategoriaMinuta } from '@/modelo/Categoria';
import { Minuta, Processo } from '@/modelo';

export interface ResultadoParseCSV {
    processos: IProcessoCSV[];
    comColunasExtras: boolean;
}

export interface ConfigImportacaoLote {
    processosCSV: IProcessoCSV[];
    csvComColunasExtras: boolean;
    servidorAtribuido: string;
    controleGabAtribuido: number;
    criarMinuta: boolean;
    tipoMinuta: number;
    modeloSelecionado: IModeloSelecionado;
    categoriasSelecionadas: CategoriaMinuta[];
    descricaoSignatario: string;
    publicavel: boolean;
    incluirAposPauta: boolean;
}

interface ResultadoImportacao {
    processosImportados: string[];
    processosJaImportados: string[];
}

class ImportacaoLoteService {

    /**
     * Lê um arquivo CSV/TXT e extrai processos.
     *
     * Formato suportado:
     * - 1 coluna: número do processo por linha
     * - 3 colunas (separadas por ; ou ,): número do processo ; cpf do servidor ; nome da tarefa
     *
     * Quando detecta 3 colunas, retorna comColunasExtras = true.
     */
    parseCSV = async (arquivo: File): Promise<ResultadoParseCSV> => {
        const conteudo = await this.lerArquivoComoTexto(arquivo);
        const linhas = conteudo.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);

        if (linhas.length === 0) {
            return { processos: [], comColunasExtras: false };
        }

        // Detecta se tem colunas extras pelo separador na primeira linha não-vazia
        const separador = this.detectarSeparador(linhas[0]);
        const primeiraLinhaColunas = linhas[0].split(separador).length;
        const comColunasExtras = primeiraLinhaColunas >= 3;

        const processos: IProcessoCSV[] = [];

        for (const linha of linhas) {
            const colunas = linha.split(separador).map(c => c.trim());
            const numeroBruto = colunas[0];

            // Extrai número completo do processo usando o parser existente
            const numerosExtraidos = ImportacaoService.extrairNumerosCompletos(numeroBruto);
            if (numerosExtraidos.length === 0) continue;

            const numeroProcesso = numerosExtraidos[0];

            if (comColunasExtras) {
                const cpfServidor = colunas[1] || undefined;
                const nomeTarefa = colunas[2] || undefined;
                processos.push({ numeroProcesso, cpfServidor, nomeTarefa });
            } else {
                processos.push({ numeroProcesso });
            }
        }

        return { processos, comColunasExtras };
    };

    /**
     * Executa o fluxo completo de importação em lote:
     * 1. Agrupa processos por (cpfServidor, tarefa) — se CSV com colunas extras
     * 2. Importa cada grupo separadamente (a API já faz distribuição)
     * 3. Aguarda conclusão da importação via polling
     * 4. Opcionalmente cria minutas para os processos importados com sucesso
     */
    executarImportacaoLote = async (config: ConfigImportacaoLote): Promise<void> => {
        const {
            processosCSV,
            csvComColunasExtras,
            servidorAtribuido,
            controleGabAtribuido,
            criarMinuta,
            tipoMinuta,
            modeloSelecionado,
            categoriasSelecionadas,
            descricaoSignatario,
            publicavel,
            incluirAposPauta,
        } = config;

        const numerosProcessos = processosCSV.map(p => p.numeroProcesso);

        runInAction(() => {
            ImportacaoLoteStore.processando = true;
            ImportacaoLoteStore.concluido = false;
            ImportacaoLoteStore.totalProcessos = numerosProcessos.length;
            ImportacaoLoteStore.processados = 0;
            ImportacaoLoteStore.minutasGeradas = 0;
            ImportacaoLoteStore.mensagens = [];
            ImportacaoLoteStore.progresso = 0;
            ImportacaoLoteStore.faseAtual = 'Importando processos...';
        });

        const erros: string[] = [];
        let processosImportadosComSucesso: string[] = [];
        let resultado: ResultadoImportacao = { processosImportados: [], processosJaImportados: [] };

        try {
            // Fase 1: Importar processos
            if (csvComColunasExtras) {
                // Agrupa por (cpfServidor, tarefa) para fazer chamadas separadas
                resultado = await this.importarPorGrupos(processosCSV, erros);
            } else {
                // Importação uniforme - todos com mesmo servidor/tarefa
                const cpfServidor = servidorAtribuido !== '-1' ? servidorAtribuido : undefined;
                const uuid = await ImportacaoApi.importarProcessos(
                    numerosProcessos,
                    'Controle de Gabinete',
                    cpfServidor,
                    controleGabAtribuido
                );
                resultado = await this.aguardarImportacao(uuid, numerosProcessos.length);

                // Distribuir processos que já estavam importados
                if (resultado.processosJaImportados.length > 0) {
                    await this.distribuirProcessosJaImportados(
                        resultado.processosJaImportados,
                        cpfServidor,
                        controleGabAtribuido,
                        erros
                    );
                }
            }

            processosImportadosComSucesso = [
                ...resultado.processosImportados,
                ...resultado.processosJaImportados,
            ];

            runInAction(() => {
                const qtdImportados = processosImportadosComSucesso.length;
                const qtdFalhas = numerosProcessos.length - qtdImportados;
                if (qtdFalhas > 0) {
                    erros.push(`${qtdFalhas} processo(s) não puderam ser importados.`);
                }
                ImportacaoLoteStore.faseAtual = criarMinuta
                    ? 'Criando minutas...'
                    : 'Finalizando...';
            });

            // Fase 2: Criar minutas (se habilitado)
            if (criarMinuta && processosImportadosComSucesso.length > 0) {
                await this.criarMinutasParaProcessos(
                    processosImportadosComSucesso,
                    tipoMinuta,
                    modeloSelecionado,
                    categoriasSelecionadas,
                    descricaoSignatario,
                    publicavel,
                    incluirAposPauta,
                    erros
                );
            }
        } catch (erro) {
            erros.push(`Erro geral: ${erro?.message ?? erro}`);
        } finally {
            runInAction(() => {
                const sucesso = processosImportadosComSucesso.length;
                const jaImportados = resultado?.processosJaImportados?.length || 0;
                const minutas = ImportacaoLoteStore.minutasGeradas;
                let resumo = `${sucesso} ${sucesso === 1 ? 'processo' : 'processos'} com sucesso.`;
                if (jaImportados > 0) {
                    resumo += ` ${jaImportados} já ${jaImportados === 1 ? 'estava importado e foi redistribuído' : 'estavam importados e foram redistribuídos'}.`;
                }
                if (config.criarMinuta) {
                    resumo += ` ${minutas} ${minutas === 1 ? 'minuta criada' : 'minutas criadas'}.`;
                }
                if (erros.length > 0) {
                    resumo += ` ${erros.length} ${erros.length === 1 ? 'erro' : 'erros'}.`;
                }
                ImportacaoLoteStore.mensagens = [resumo, ...erros];
                ImportacaoLoteStore.progresso = 100;
                ImportacaoLoteStore.processando = false;
                ImportacaoLoteStore.concluido = true;
                ImportacaoLoteStore.faseAtual = 'Concluído';
            });

            // Atualiza dados do escaninho
            try {
                await EscaninhoService.carregarProcessosDoGabinete();
            } catch {
                // Não impede a conclusão
            }
        }
    };

    /**
     * Importa processos agrupados por (cpfServidor, nomeTarefa).
     * Cada grupo gera uma chamada separada à API de importação.
     */
    private importarPorGrupos = async (processosCSV: IProcessoCSV[], erros: string[]): Promise<ResultadoImportacao> => {
        // Agrupa por chave (cpfServidor + nomeTarefa)
        const grupos = new Map<string, IProcessoCSV[]>();
        for (const proc of processosCSV) {
            const chave = `${proc.cpfServidor || ''}|${proc.nomeTarefa || ''}`;
            if (!grupos.has(chave)) {
                grupos.set(chave, []);
            }
            grupos.get(chave).push(proc);
        }

        const totalProcessos = processosCSV.length;
        let processadosAteAgora = 0;
        const todosImportados: string[] = [];
        const todosJaImportados: string[] = [];

        for (const [, processos] of grupos) {
            const primeiro = processos[0];
            const cpfServidor = primeiro.cpfServidor || undefined;
            const nomeTarefa = primeiro.nomeTarefa;

            // Resolve o número do controle de gabinete a partir do nome da tarefa
            let numeroControleGab = -1;
            if (nomeTarefa) {
                const controle = ControleGabineteStore.buscarPorDescricao(nomeTarefa);
                if (controle) {
                    numeroControleGab = controle.numero;
                } else {
                    // Tarefa não encontrada — registra erro para todos os processos do grupo
                    processos.forEach(p => {
                        erros.push(`${p.numeroProcesso} - Tarefa "${nomeTarefa}" não encontrada.`);
                    });
                    processadosAteAgora += processos.length;
                    runInAction(() => {
                        ImportacaoLoteStore.processados = processadosAteAgora;
                        ImportacaoLoteStore.progresso = Math.min(
                            (processadosAteAgora / totalProcessos) * (ImportacaoLoteStore.criarMinuta ? 50 : 100),
                            ImportacaoLoteStore.criarMinuta ? 50 : 99
                        );
                    });
                    continue;
                }
            }

            const numerosDoGrupo = processos.map(p => p.numeroProcesso);

            try {
                const uuid = await ImportacaoApi.importarProcessos(
                    numerosDoGrupo,
                    'Controle de Gabinete',
                    cpfServidor,
                    numeroControleGab
                );

                const resultadoGrupo = await this.aguardarImportacao(uuid, numerosDoGrupo.length);
                todosImportados.push(...resultadoGrupo.processosImportados);
                todosJaImportados.push(...resultadoGrupo.processosJaImportados);

                // Distribuir processos que já estavam importados para o servidor/tarefa do grupo
                if (resultadoGrupo.processosJaImportados.length > 0) {
                    await this.distribuirProcessosJaImportados(
                        resultadoGrupo.processosJaImportados,
                        cpfServidor,
                        numeroControleGab,
                        erros
                    );
                }
            } catch (erro) {
                processos.forEach(p => {
                    erros.push(`${p.numeroProcesso} - Erro na importação: ${erro?.message ?? erro}`);
                });
            }

            processadosAteAgora += processos.length;
            runInAction(() => {
                ImportacaoLoteStore.processados = processadosAteAgora;
                ImportacaoLoteStore.progresso = Math.min(
                    (processadosAteAgora / totalProcessos) * (ImportacaoLoteStore.criarMinuta ? 50 : 100),
                    ImportacaoLoteStore.criarMinuta ? 50 : 99
                );
                ImportacaoLoteStore.faseAtual = `Importando processos: ${processadosAteAgora} de ${totalProcessos}`;
            });
        }

        return { processosImportados: todosImportados, processosJaImportados: todosJaImportados };
    };

    /**
     * Aguarda a importação concluir via polling do status.
     * Retorna os números de processos importados com sucesso e os que já estavam importados.
     */
    private aguardarImportacao = async (uuid: string, total: number): Promise<ResultadoImportacao> => {
        const maxTentativas = 120; // 10 min max (5s * 120)
        const intervalo = 5000;
        let tentativas = 0;
        const processosImportados: string[] = [];
        const processosJaImportados: string[] = [];

        while (tentativas < maxTentativas) {
            await this.delay(intervalo);
            tentativas++;

            try {
                const statusProcessos = await ImportacaoApi.verificarProcessosImportados(uuid);
                const concluidos = statusProcessos.filter(s =>
                    s.txtEstado && !s.txtEstado.toLowerCase().includes('aguardando')
                );

                processosImportados.length = 0;
                processosJaImportados.length = 0;

                concluidos.forEach(s => {
                    if (s.txtEstado.toLowerCase().includes('erro')) {
                        // Processo com erro - não inclui em nenhuma lista de sucesso
                    } else if (s.txtEstado === 'Processo já importado no gabinete') {
                        processosJaImportados.push(s.numeroProcesso);
                    } else {
                        processosImportados.push(s.numeroProcesso);
                    }
                });

                if (concluidos.length >= total) {
                    break;
                }
            } catch {
                // Erro no polling, tenta novamente
            }
        }

        return { processosImportados, processosJaImportados };
    };

    /**
     * Distribui processos que já estavam importados no gabinete para o servidor/tarefa indicados.
     * Busca o processo completo (com controleGabineteAtual) e usa ProcessoService.associarProcessoAServidor.
     */
    private distribuirProcessosJaImportados = async (
        numerosProcessos: string[],
        cpfServidor: string | undefined,
        numeroControleGab: number,
        erros: string[]
    ): Promise<void> => {
        for (const numeroProcesso of numerosProcessos) {
            try {
                const idProcesso = await ProcessoService.idProcessoPorNumero(numeroProcesso);
                if (!idProcesso) {
                    erros.push(`${numeroProcesso} - Processo já importado, mas não encontrado para distribuição.`);
                    continue;
                }

                // Busca o processo completo para obter o controleGabinete atual
                const processos = await ProcessoService.processoPorIdFiltroRapido(
                    UsuarioStore.gabinete,
                    idProcesso
                );
                const processo = processos?.[0];
                if (!processo) {
                    erros.push(`${numeroProcesso} - Processo já importado, mas não encontrado no gabinete para distribuição.`);
                    continue;
                }

                await ProcessoService.associarProcessoAServidor(
                    UsuarioStore.gabinete,
                    cpfServidor || null,
                    numeroControleGab,
                    [processo],
                    ''
                );
            } catch (erro) {
                erros.push(`${numeroProcesso} - Erro ao distribuir processo já importado: ${erro?.message ?? erro}`);
            }
        }
    };

    /**
     * Cria minutas para cada processo importado com sucesso.
     */
    private criarMinutasParaProcessos = async (
        numerosProcessos: string[],
        tipoMinuta: number,
        modeloSelecionado: IModeloSelecionado,
        categoriasSelecionadas: CategoriaMinuta[],
        descricaoSignatario: string,
        publicavel: boolean,
        incluirAposPauta: boolean,
        erros: string[]
    ): Promise<void> => {
        const total = numerosProcessos.length;
        let processados = 0;

        const timeoutPadraoAxios = axios.defaults.timeout;
        axios.defaults.timeout = 120000;

        try {
            for (const numeroProcesso of numerosProcessos) {
                try {
                    const idProcesso = await ProcessoService.idProcessoPorNumero(numeroProcesso);
                    if (!idProcesso) {
                        erros.push(`${numeroProcesso} - Processo não encontrado após importação.`);
                        continue;
                    }

                    const processo = Processo.novo({ identificadorProcesso: idProcesso, numeroProcesso });
                    const minuta = Minuta.criar({});
                    minuta.tipo = { codigo: tipoMinuta, descricao: '' };

                    const modeloDaGeracao = {
                        nome: modeloSelecionado.nome,
                        tipoMinuta: null,
                        codigo: modeloSelecionado.codigo ?? 0,
                        incluirAposPauta: incluirAposPauta || modeloSelecionado.incluirAposPauta,
                    };

                    await MinutaService.salvarMinutaAPartirDeModelo(
                        processo,
                        minuta,
                        modeloDaGeracao,
                        UsuarioStore.login,
                        false,
                        false,
                        descricaoSignatario
                    );

                    await MinutaService.salvarMinutaDados(
                        processo.identificadorProcesso,
                        null,
                        minuta.tipo,
                        UsuarioStore.usuario.login,
                        UsuarioStore.gabinete,
                        publicavel,
                        undefined,
                        processo.identificadorProcesso
                    );

                    await MinutaService.aplicarAposPautaSeNecessario(
                        processo,
                        minuta,
                        modeloDaGeracao,
                        UsuarioStore.login
                    );

                    if (descricaoSignatario) {
                        await MinutaService.gravarFuncaoMagistrado(
                            processo.identificadorProcesso,
                            tipoMinuta,
                            descricaoSignatario
                        );
                    }

                    // Associar categorias
                    if (categoriasSelecionadas.length > 0) {
                        await CategoriaMinutaService.associarCategoriasAMinuta(
                            processo,
                            minuta,
                            categoriasSelecionadas
                        );
                    }

                    await ProcessoService.atualizarDadosDoProcesso(idProcesso);
                    await EscaninhoService.atualizarProcesso(idProcesso, true);

                    runInAction(() => {
                        ImportacaoLoteStore.minutasGeradas++;
                    });
                } catch (erro) {
                    erros.push(`${numeroProcesso} - ${erro?.message ?? erro}`);
                } finally {
                    processados++;
                    runInAction(() => {
                        ImportacaoLoteStore.processados = ImportacaoLoteStore.totalProcessos + processados;
                        ImportacaoLoteStore.progresso = 50 + (processados / total) * 50;
                        ImportacaoLoteStore.faseAtual = `Criando minutas: ${processados} de ${total}`;
                    });
                }
            }
        } finally {
            axios.defaults.timeout = timeoutPadraoAxios;
        }
    };

    /**
     * Detecta o separador do CSV. Se contém ';', usa ';'. Senão, usa ','.
     */
    private detectarSeparador = (linha: string): string => {
        if (linha.includes(';')) return ';';
        if (linha.includes(',')) return ',';
        return ';';
    };

    private lerArquivoComoTexto = (arquivo: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error('Erro ao ler o arquivo'));
            reader.readAsText(arquivo);
        });
    };

    private delay = (ms: number): Promise<void> => {
        return new Promise(resolve => setTimeout(resolve, ms));
    };
}

export default new ImportacaoLoteService();
